<template>
  <div
    class="chips-group relative flex flex-row flex-wrap gap-2 items-center"
    :class="{
      [`ak-${props.size}`]: !!props.size,
    }"
  >
    <span v-for="chip in props.items" class="flex-shrink-0">
      <NuxtLinkLocale
        v-if="!chip.disabled"
        :locale="switchBlogLocale() ?? undefined"
        :to="chip.redirectTo ?? '/'"
        class="chip flex flex-row flex-nowrap items-center"
        :class="{
          __active: chip.key === props.active,
        }"
      >
        <NuxtImg
          loading="lazy"
          v-if="!!chip.iconURL"
          :src="chip.iconURL"
          alt=""
          class="flex-shrink-0 h-[1.4em]"
          width="22"
          height="22"
        />
        <span>{{ chip.label ?? chip.key }}</span>
      </NuxtLinkLocale>
    </span>
  </div>
</template>

<script setup lang="ts">
export interface ChipsGroupItem {
  key: string;
  label?: string;
  redirectTo?: string;
  iconURL?: string;
  disabled?: boolean;
}

export interface ChipsGroupProps<T extends ChipsGroupItem>
  extends ChipsGroupEmits {
  items: T[];
  active: ChipsGroupProps<T>["items"][number]["key"];
  size?: "small" | "medium" | "large";
}
const props = withDefaults(defineProps<ChipsGroupProps<ChipsGroupItem>>(), {
  size: "medium",
});

interface ChipsGroupEmits {
  (eventName: "change", event: PointerEvent, ...args: unknown[]): void;
}
const emits = defineEmits<ChipsGroupEmits>();
</script>

<style>
.chips-group.ak-small {
  --chip-size: 0.75em;
  --chip-spacing-x: 0.75em;
  --chip-spacing-y: 0.25em;
}

.chips-group.ak-medium {
  --chip-size: 1em;
  --chip-spacing-x: 0.75em;
  --chip-spacing-y: 0.25em;
}

.chips-group.ak-large {
  --chip-size: 1em;
  --chip-spacing-x: 1em;
  --chip-spacing-y: 0.4em;
}

.chips-group > span:blank,
.chips-group > span:empty {
  background-color: red;
}

.chips-group .chip {
  @apply border border-primary font-semibold transition hover:bg-primary/10 focus-visible:bg-primary/10;

  border-radius: 2em;
  font-size: var(--chip-size);
  padding-inline: var(--chip-spacing-x);
  padding-block: var(--chip-spacing-y);

  gap: calc(var(--chip-spacing-x) / 1.5);
}

.chips-group .chip.__active {
  @apply text-white bg-secondary hover:bg-secondary/90 focus-visible:bg-secondary/90;
  border-color: transparent;
}

.chips-group .chip.__active img {
  filter: brightness(0) invert(1);
}
</style>
